import * as React from "react";
import { FieldProps } from "formik";
import { Form } from "antd";
import { useState, useRef } from "react";

const FormItem = Form.Item;

export const InputPinField: React.FC<
  FieldProps<any> & {
    description?: string;
    prefix: React.ReactNode;
    label?: string;
    useNumberComponent?: boolean;
    className?: string;
    pinLength?: number;
    disabled?: boolean;
    autoFocus?: boolean;
  }
> = ({
  field: { onChange, ...field },
  form: { touched, errors, setFieldValue, setFieldTouched },
  label,
  useNumberComponent = false,
  className = "",
  pinLength = 4,
  disabled = false,
  autoFocus = true,
  description,
  ...props
}) => {
  const [pin, setPin] = useState<string[]>(new Array(pinLength).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(new Array(pinLength).fill(null));

  const errorMsg = touched[field.name] && errors[field.name];

  // PIN input handlers
  const handlePinChange = (index: number, val: string) => {
    const digit = val.replace(/[^0-9]/g, "");
    if (digit.length > 1) return;

    // Find the first empty position
    let targetIndex = -1;
    for (let i = 0; i < pinLength; i++) {
      if (pin[i] === "") {
        targetIndex = i;
        break;
      }
    }

    // If no empty position found, don't allow input
    if (targetIndex === -1) return;

    const newPin = [...pin];
    newPin[targetIndex] = digit;
    setPin(newPin);

    const pinString = newPin.join("");
    setFieldValue(field.name, pinString);

    // Auto-focus next input if available
    if (targetIndex < pinLength - 1) {
      inputRefs.current[targetIndex + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      // Find the last filled position
      let targetIndex = -1;
      for (let i = pinLength - 1; i >= 0; i--) {
        if (pin[i] !== "") {
          targetIndex = i;
          break;
        }
      }

      if (targetIndex !== -1) {
        const newPin = [...pin];
        newPin[targetIndex] = "";
        setPin(newPin);
        setFieldValue(field.name, newPin.join(""));
        
        // Focus the cleared input
        inputRefs.current[targetIndex]?.focus();
      }
    }
  };

  const getPinInputClasses = (hasValue: boolean) => {
    let classes = "w-[15px] h-[15px] text-center text-2xl font-bold rounded-full border-0 transition-all duration-300 focus:outline-none";
    
    if (hasValue) {
      classes += " bg-[color:var(--brand-primary)] text-white";
    } else {
      classes += " bg-[#1E1E1E] text-white";
    }
    
    return classes;
  };

  return (
    <FormItem
      label={label}
      help={
        errorMsg ? (
          <div style={{ textAlign: "center" }} className="text-red-500 text-sm mt-1">
            {typeof errorMsg === "string"
                ? errorMsg
                : Array.isArray(errorMsg)
                ? errorMsg.join(", ")
                : null}
          </div>
        ) : null
      }
      validateStatus={errorMsg ? "error" : undefined}
    >
      <div className="w-full">
        <div className="flex justify-center items-center gap-4">
          {pin.map((digit, index) => {
            const hasValue = digit !== "";
            return (
              <input
                key={index}
                ref={(ref) => {
                  inputRefs.current[index] = ref;
                }}
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value=""
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handlePinKeyDown(index, e)}
                className={getPinInputClasses(hasValue)}
                maxLength={1}
                style={{ caretColor: "transparent" }}
              />
            );
          })}
        </div>
      </div>
    </FormItem>
  );
};
